import _ from 'lodash'
import type { KBRow } from './detail.config'
import { type DocumentStatus, KBDocumentSource, type RKnowledgeBase } from './types'

/**
 * Builds document rows for sources that should display documents as subRows (url and app sources)
 */
export const buildDocumentRows = (
  documents: RKnowledgeBase['sources'][0]['documents'],
  source: RKnowledgeBase['sources'][0],
  knowledgeBase: RKnowledgeBase,
): KBRow[] =>
  (documents || []).map((doc) => ({
    id: doc.id,
    name: doc.display_name || '',
    display_name: doc.display_name || '',
    embedding_status: doc.embedding_status,
    source: source.source_type,
    last_updated_on: doc.updated_at || knowledgeBase.updated_at,
    isSource: false,
    enabled: doc.enabled,
    // Same fallback as file docs (buildIndividualDocumentRows): a worker-ingested doc has no acting
    // user (updated_by null) and only gained a real timestamp once rag started serializing it — fall
    // back to the KB-level editor/time so app/url sub-docs never render blank Updated By / Updated At.
    updated_by: doc.updated_by?.name ? doc.updated_by : knowledgeBase.updated_by,
    status: doc.embedding_status,
    updated_at: doc.updated_at || knowledgeBase.updated_at,
    url: doc.id,
    parentKnowledgeBaseId: knowledgeBase.id,
    fetching: source.fetching,
    app_source: source.config.app_source ?? '',
    error_detail: doc.embedding_error ?? null,
    document_name: doc.name,
    chunk_count: doc.chunk_count,
    is_empty: doc.is_empty ?? false, // benign no-content page → muted "Empty", not a red "No content"
    download_url: doc.download_url ?? null,
  }))

/**
 * Builds a source row with its documents as subRows (for url and app sources)
 */
export const buildSourceRowWithSubRows = (
  source: RKnowledgeBase['sources'][0],
  knowledgeBase: RKnowledgeBase,
): KBRow => ({
  id: source.id,
  name: source.config.url || source.config.app_source || '',
  display_name: source.config.url || _.capitalize(source.config.app_source || '') || '',
  embedding_status: source.embedding_status as DocumentStatus,
  source: source.source_type,
  last_updated_on: knowledgeBase.updated_at,
  isSource: true,
  url: source.config.url || '',
  parentKnowledgeBaseId: knowledgeBase.id,
  fetching: source.fetching,
  status: source.embedding_status as DocumentStatus,
  error_detail: source.error_detail ?? null,
  subRows: buildDocumentRows(source.documents, source, knowledgeBase),
  // TRUE totals (subRows is the capped top-N display list). The badge shows document_count; when it
  // exceeds the rendered rows the cell notes "showing first M"; error_count drives the "K failed" badge;
  // empty_count drives a muted "K empty" (benign no-content pages, NOT failures).
  document_count: source.document_count ?? source.documents.length,
  error_count: source.error_count ?? 0,
  empty_count: source.empty_count ?? 0,
  app_source: source.config.app_source ?? '',
  app_source_id: source.config.app_source_id ?? undefined,
  enabled: source.enabled,
  updated_by: knowledgeBase.updated_by,
  updated_at: knowledgeBase.updated_at,
})

/**
 * Builds individual document rows (for sources other than url and app)
 */
export const buildIndividualDocumentRows = (
  source: RKnowledgeBase['sources'][0],
  knowledgeBase: RKnowledgeBase,
): KBRow[] =>
  (source.documents || []).map((doc) => ({
    id: doc.id,
    name: doc.display_name || '',
    display_name: doc.display_name || '',
    embedding_status: doc.embedding_status,
    source: source.source_type,
    last_updated_on: doc.updated_at || knowledgeBase.updated_at,
    isSource: false,
    enabled: doc.enabled,
    // A file's Document row is created by the WORKER, which has no acting-user, so doc.updated_by is
    // empty and rag's /status serializer omits doc.updated_at. Fall back to the KB-level values (who
    // last touched the KB / when) so a file keeps its Updated By / Updated At AFTER it finishes
    // ingesting — matching how url/app source rows already show KB-level values. (Before this, a file
    // showed everything while Pending, then went blank once it went Active and got a Document row.)
    updated_by: doc.updated_by?.name ? doc.updated_by : knowledgeBase.updated_by,
    status: doc.embedding_status,
    updated_at: doc.updated_at || knowledgeBase.updated_at,
    url: doc.id,
    parentKnowledgeBaseId: knowledgeBase.id,
    fetching: source.fetching,
    app_source: source.config.app_source ?? '',
    error_detail: doc.embedding_error ?? null,
    document_name: doc.name,
    chunk_count: doc.chunk_count,
    download_url: doc.download_url ?? null,
  }))

/**
 * Builds a standalone row for a FILE source that has no documents yet — i.e. it is still ingesting
 * or it FAILED before producing a document. Without this such a source renders nothing (the file
 * appears to vanish after upload), hiding both the failure and the ability to retry/delete it.
 */
export const buildFileSourceRow = (
  source: RKnowledgeBase['sources'][0],
  knowledgeBase: RKnowledgeBase,
): KBRow => {
  const label =
    source.config.original_filename || source.config.url?.split('/').pop() || 'Uploaded file'
  return {
    id: source.id,
    name: label,
    display_name: label,
    embedding_status: source.embedding_status as DocumentStatus,
    source: source.source_type,
    last_updated_on: knowledgeBase.updated_at,
    isSource: true,
    url: source.config.url || '',
    parentKnowledgeBaseId: knowledgeBase.id,
    fetching: source.fetching,
    app_source: source.config.app_source ?? '',
    status: source.embedding_status as DocumentStatus,
    error_detail: source.error_detail ?? null,
    enabled: source.enabled,
    updated_by: knowledgeBase.updated_by,
    updated_at: knowledgeBase.updated_at,
  }
}

/**
 * Builds table rows from knowledge base sources
 */
export const buildTableRows = (knowledgeBase: RKnowledgeBase | undefined): KBRow[] => {
  if (!knowledgeBase?.sources) return []

  const rows: KBRow[] = []

  knowledgeBase.sources.forEach((source) => {
    // For url and app sources, return documents as subRows
    if (
      source.source_type === KBDocumentSource.URL ||
      source.source_type === KBDocumentSource.APP
    ) {
      rows.push(buildSourceRowWithSubRows(source, knowledgeBase))
    } else {
      // File sources: one row per document. When there are none (still ingesting, or failed before
      // producing a document), fall back to a source-level row so the file is still visible with its
      // status/error and can be retried or deleted.
      const docRows = buildIndividualDocumentRows(source, knowledgeBase)
      rows.push(...(docRows.length ? docRows : [buildFileSourceRow(source, knowledgeBase)]))
    }
  })

  return rows
}
